import { useCallback, useEffect, useState } from 'react';

import { MediaPermissions } from '../constants';

const stopStream = mediaStream => {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop());
  }
};

export const useQueryDevice = showError => {
  const [permission, setPermission] = useState({ cameraPermission: null, micPermission: null });

  const getDeniedSystemMicrophonePermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      // Errors when accessing the device
      setPermission({
        micPermission: MediaPermissions.SystemPermissionDenied,
        cameraPermission: null,
      });
    }
  }, []);

  const getDeniedSystemCameraPermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      getDeniedSystemMicrophonePermission();
    } catch (error) {
      // Errors when accessing the device
      setPermission({
        cameraPermission: MediaPermissions.SystemPermissionDenied,
        micPermission: null,
      });
    }
  }, [getDeniedSystemMicrophonePermission]);

  const handlePermissionDenined = useCallback(
    error => {
      if (error.name === 'NotAllowedError') {
        if (error.message.includes('Permission denied by system')) {
          getDeniedSystemCameraPermission();
        } else {
          setPermission({
            cameraPermission: MediaPermissions.UserPermissionDenied,
            micPermission: MediaPermissions.UserPermissionDenied,
          });
        }
      } else if (error.name === 'NotFoundError') {
        showError('Camera or microphone not found. Please check your device');
        setPermission({
          cameraPermission: MediaPermissions.NotFoundError,
          micPermission: MediaPermissions.NotFoundError,
        });
      } else if (error.name === 'NotReadableError') {
        showError(
          'Your camera or microphone is already in use other application or broken. Please try again',
        );
        setPermission({
          cameraPermission: MediaPermissions.NotReadableError,
          micPermission: MediaPermissions.NotReadableError,
        });
      } else {
        showError('There are some errors. Please try again');
        setPermission({
          cameraPermission: MediaPermissions.Others,
          micPermission: MediaPermissions.Others,
        });
      }
    },
    [getDeniedSystemCameraPermission],
  );

  useEffect(() => {
    let mediaStream = null;
    async function queryDevices() {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setPermission({
          cameraPermission: MediaPermissions.PermissionGranted,
          micPermission: MediaPermissions.PermissionGranted,
        });
      } catch (error) {
        // Errors when accessing the device
        handlePermissionDenined(error);
      }
    }

    queryDevices();

    return () => {
      stopStream(mediaStream);
      mediaStream = null;
    };
  }, [handlePermissionDenined]);

  return permission;
};
